// import { ref } from 'vue'
import DXPService from '@/modules/core/services/DXPService'
import MiddlewareService from '@/modules/core/services/MiddlewareService'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useToastStore } from '@/modules/core/stores/toastStore'
import { base64ToBlob, fileToBase64, stringToFile } from '@/utils/converterUtils'
import { defineStore } from 'pinia'
import { inject, ref } from 'vue'
import { useRouter } from 'vue-router'
import { servicesMock } from '../../../mocks/serviceMockData'
import { useErrorManagerComposable } from '../composables/errorManagerComposable'
import ENDPOINTSAPI from '../constants/apiEndpoints'
import ENDPOINTSMOCK from '../constants/apiEndpoints.mocks'
import { ButtonTypeEnum, InsuranceState, MessageEnum, ServiceErrorCodeEnum } from '../models/enums'
import { useBusinessStore } from './businessStore'
import { useSupportModalStore } from './supportModalStore'

import CommonCommonMapper from '@/modules/common/common/mappers/index'
import type CoverageBasic from '@/modules/common/common/models/APP/CoverageBasic'
import type {
  BusinessLineInterface,
  BusinessLinesInterface,
  CommonConfigsInterface,
  FooterLinksInterface
} from '@/modules/common/common/models/DXP/BranchesData'
import type GetCoveragesBasicResponse from '@/modules/common/common/models/DXP/responses/GetCoveragesBasicResponse'
import GetCoveragesCompleteRequest from '@/modules/common/common/models/MID/requests/GetCoveragesCompleteRequest'
import GetCoveragesCompleteResponse from '@/modules/common/common/models/MID/responses/GetCoveragesCompleteResponse'
import type BankDetails from '@/modules/common/hiring/models/APP/BankDetails'
import CustomError from '@/modules/core/models/errors/CustomError'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import type { MDWCityInterface } from '@/modules/core/models/types/middleware'
import type { VueCookies } from 'vue-cookies'
import CommonMapper from '../mappers/CommonMapper'
import { DXPConfigPathEnum, ErrorDisplayTypeEnum } from '../models/enums'
import type { itemCountryPrefix, OptionsValuesInterface } from '../models/types'
import UserInfo from '../models/UserInfo'
import InfoMediator from '@/modules/common/common/models/MID/responses/GetInfoMediatorResponse'

const MEDIATOR_BUSINESS_LINE_MAP: Record<string, keyof InfoMediator['allowedBusinessLines']> = {
  health: 'health',
  'health-foreign': 'foreignHealth',
  dental: 'dental',
  pets: 'pets',
  travel: 'travel',
  life: 'life',
  home: 'home'
}

export const useCommonStore = defineStore('commonStore', () => {
  const errorManager = useErrorManagerComposable()
  const loadingStore = useLoadingStore()
  const toastStore = useToastStore()
  const businessLineStore = useBusinessStore()
  const supportModalStore = useSupportModalStore()

  const showLogo = ref<boolean>(false)
  const referrer = ref<string>('')
  const loaderMessage = ref<string | null>(null)
  const businessLines = ref<BusinessLinesInterface | null>(null)
  const pymesBusinessLines = ref<BusinessLinesInterface | null>(null)
  const footerLinks = ref<FooterLinksInterface[] | null>(null)
  const commonConfigs = ref<CommonConfigsInterface | null>(null)
  const provincesData = ref<{ code: string; description: string }[]>([])
  const countries = ref<OptionsValuesInterface[]>([])
  const countryPrefixes = ref<OptionsValuesInterface[]>([])
  const abortController = ref<AbortController | null>(null)
  const router = useRouter()
  const userInfo = ref<UserInfo | null>(null)
  const currentPageDXP = ref<string>('https://www.asisa.es')
  const infoMediator = ref<InfoMediator | null>(null)
  const $cookies = inject<VueCookies>('$cookies')

  const ENDPOINTS = !loadingStore.isActiveMock()
    ? { ...ENDPOINTSAPI }
    : { ...ENDPOINTSAPI, ...ENDPOINTSMOCK }
  const mock = loadingStore.isActiveMock()

  // ✅
  async function fetchBranchesData() {
    if (businessLines.value) {
      return businessLines.value
    }

    const endpoint = ENDPOINTS.DXP_GET_BRANCHES_DATA
    const { error, data } = await DXPService.get(endpoint)
    if (error) {
      throw new ServiceError(data)
        .setDisplayType(ErrorDisplayTypeEnum.MODAL)
        .setTitle('copy_error_service_title')
        .setModalDescription('copy_error_service_description')
        .setButtons([
          new ModalButton({
            text: 'copy_button_return',
            navTo: 'exit',
            type: ButtonTypeEnum.OUTLINE
          })
        ])
    }
    // order business lines object keys by every business line order property
    const businessLinesOrdered = Object.fromEntries(
      Object.entries(data.businessLines).sort(([, valueA], [, valueB]) => {
        const orderA = (valueA as BusinessLineInterface).order ?? Number.MAX_SAFE_INTEGER
        const orderB = (valueB as BusinessLineInterface).order ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB
      })
    )
    businessLines.value = businessLinesOrdered as unknown as BusinessLinesInterface
    commonConfigs.value = data['common-configs']

    return businessLines.value
  }

  async function fetchPymesBranchesData() {
    if (pymesBusinessLines.value) {
      return pymesBusinessLines.value
    }

    const { error, data } = await DXPService.post(ENDPOINTS.DXP_GET_BRANCH_CONFIG, {
      path: DXPConfigPathEnum.PYMES
    })

    if (error) {
      throw new ServiceError(data)
        .setDisplayType(ErrorDisplayTypeEnum.MODAL)
        .setTitle('copy_error_service_title')
        .setModalDescription('copy_error_service_description')
        .setButtons([
          new ModalButton({
            text: 'copy_button_return',
            navTo: 'exit',
            type: ButtonTypeEnum.OUTLINE
          })
        ])
    }
    // order business lines object keys by every business line order property
    const pymesBusinessLinesOrdered = Object.fromEntries(
      Object.entries(data.businessLines).sort(([, valueA], [, valueB]) => {
        const orderA = (valueA as BusinessLineInterface).order ?? Number.MAX_SAFE_INTEGER
        const orderB = (valueB as BusinessLineInterface).order ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB
      })
    )
    pymesBusinessLines.value = pymesBusinessLinesOrdered as unknown as BusinessLinesInterface
    commonConfigs.value = data['common-configs']

    return pymesBusinessLines.value
  }

  async function fetchFooterLinks() {
    if (footerLinks.value) {
      return footerLinks.value
    }
    const endpoint = ENDPOINTS.DXP_GET_FOOTER_LINKS
    const { error, data } = await DXPService.get(endpoint)
    if (error)
      throw new ServiceError(data)
        .setDisplayType(ErrorDisplayTypeEnum.MODAL)
        .setTitle('copy_error_service_title')
        .setModalDescription('copy_error_service_description')
        .setButtons([
          new ModalButton({
            text: 'copy_button_return',
            navTo: 'exit',
            type: ButtonTypeEnum.OUTLINE
          })
        ])
    footerLinks.value = data
    return footerLinks.value
  }

  async function loadBranchesConfig(isPymes = false) {
    try {
      loadingStore.startLoading()
      await Promise.all([
        fetchFooterLinks(),
        isPymes ? fetchPymesBranchesData() : fetchBranchesData()
      ])
    } catch (error) {
      errorManager.handleError(error)
    } finally {
      loadingStore.stopLoading()
    }
  }

  function setUrlDxp() {
    const currentPageUrl = $cookies?.get('currentPageUrl')
    currentPageDXP.value = currentPageUrl ? atob(currentPageUrl) : 'https://www.asisa.es' //GET COOKIES
  }
  function getUrlDxp() {
    return currentPageDXP.value
  }
  function getReferred() {
    return referrer.value
  }
  function setReferred(value: string) {
    referrer.value = value
  }
  function getCommonConfigs() {
    return commonConfigs.value
  }

  function getContactPhone(): string {
    return router.currentRoute.value.meta?.phone as string
  }

  function getUserInfo() {
    if (userInfo.value) {
      return userInfo.value
    }
    userInfo.value = new UserInfo()
    return userInfo.value
  }

  async function fetchProvinceCodes() {
    try {
      if (provincesData.value.length > 0) {
        return provincesData.value
      }

      const endpoint = ENDPOINTS.DXP_GET_PROVINCES
      const { error, data } = await DXPService.get(endpoint)
      if (error) throw new ServiceError(data)

      provincesData.value = data.asisaProvinces.provinces

      return provincesData.value
    } catch (error) {
      errorManager.handleError(error)

      return []
    }
  }

  async function fetchCountries(): Promise<OptionsValuesInterface[]> {
    try {
      if (countries.value.length > 0) {
        return countries.value
      }

      const endpoint = ENDPOINTS.DXP_GET_COUNTRIES
      const { error, data } = await DXPService.get(endpoint)
      if (error) throw new ServiceError(data)

      const countriesList = data?.asisaCountries.countries
      countries.value = countriesList
        .map((c: any) => ({
          label: c.description,
          value: c.code
        }))
        .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label))
      return countries.value
    } catch (error) {
      errorManager.handleError(error)

      return []
    }
  }

  async function fetchCountryPrefixes(): Promise<OptionsValuesInterface[]> {
    try {
      if (countryPrefixes.value.length > 0) {
        return countryPrefixes.value
      }

      const endpoint = ENDPOINTS.DXP_GET_COUNTRIES
      const { error, data } = await DXPService.get(endpoint)
      if (error) throw new ServiceError(data)

      const rawList: itemCountryPrefix[] = (data?.asisaCountries?.countries ?? []).filter(
        (c: itemCountryPrefix) => (c?.prefix ?? '') !== ''
      )

      const groupedByPrefix = Object.values(
        rawList.reduce((acc: Record<string, any>, curr: itemCountryPrefix) => {
          const key = String(curr.prefix).trim()
          if (!acc[key]) {
            acc[key] = {
              prefix: key,
              description: (curr.description ?? '').trim(),
              code: [] as string[]
            }
          }

          const code = curr.code
          if (code && !acc[key].code.includes(code)) {
            acc[key].code.push(code)
          }

          const existing = acc[key].description
            ? acc[key].description
                .split('/')
                .map((s: string) => s.trim())
                .filter(Boolean)
            : []
          const nextSet = new Set(existing)
          if (curr.description) nextSet.add(curr.description.trim())
          acc[key].description = Array.from(nextSet).join(' / ')

          return acc
        }, {})
      ).sort((a: { prefix: string }, b: { prefix: string }) => Number(a.prefix) - Number(b.prefix))

      countryPrefixes.value = groupedByPrefix.map((country: any) => {
        return {
          label: `+${country.prefix} (${country.description})`,
          value: `+${country.prefix}`,
          extra: {
            code: country.code as string[]
          }
        }
      })

      return countryPrefixes.value
    } catch (error) {
      errorManager.handleError(error)
      return []
    }
  }

  async function findCities(
    provinceCode: string,
    cityAutocomplete: string
  ): Promise<{ values: MDWCityInterface[]; aborted: boolean }> {
    try {
      if (cityAutocomplete.length < 2) {
        return { values: [], aborted: false }
      }

      if (!provinceCode) {
        throw new Error('Invalid parameters')
      }

      if (abortController.value) {
        abortController.value.abort()
      }

      abortController.value = new AbortController()

      const endpoint = ENDPOINTS.AUTOCOMPLETE_CITY
      const request = { provinceCode, cityAutocomplete }
      const config = {
        mock: mock && servicesMock.AUTOCOMPLETE_CITY,
        signal: abortController.value.signal
      }
      const { error, data, aborted } = await MiddlewareService.post(endpoint, request, config)

      if (aborted) return { values: [], aborted: true }

      if (error) throw new ServiceError(data)

      const values = data.filter((d: any) => d.city !== null)
      return { values, aborted }
    } catch (error: ServiceError | any) {
      errorManager.handleError(error)

      return { values: [], aborted: error?.aborted ?? false }
    }
  }

  // ✅
  async function findStreets(
    provinceCode: string,
    cityCode: string,
    street: string
  ): Promise<{ values: string[]; aborted: boolean }> {
    try {
      if (street.length < 2) {
        return { values: [], aborted: false }
      }

      if (!provinceCode || !cityCode) {
        throw new Error('Invalid parameters')
      }

      if (abortController.value) {
        abortController.value.abort()
      }

      abortController.value = new AbortController()

      const endpoint = ENDPOINTS.AUTOCOMPLETE_STREET
      const request = { provinceCode, cityCode, street }
      const config = {
        mock: mock && servicesMock.AUTOCOMPLETE_STREET,
        signal: abortController.value.signal
      }
      const { error, data, aborted } = await MiddlewareService.post(endpoint, request, config)

      if (aborted) return { values: [], aborted: true }

      if (error) throw new ServiceError(data)

      return { values: data, aborted }
    } catch (error: ServiceError | any) {
      errorManager.handleError(error)

      return { values: [], aborted: false }
    }
  }

  async function normalizeAddress({
    street,
    city,
    zip,
    house // streetType
  }: Record<string, string>) {
    try {
      if (!street || !city || !zip || !house) {
        throw new Error('Invalid parameters')
      }

      const endpoint = ENDPOINTS.NORMALIZE_ADDRESS
      const request = { street, city, zip, house /*, streetType*/ }
      const config = { mock: mock && servicesMock.NORMALIZE_ADDRESS }
      const { error, data } = await MiddlewareService.post(endpoint, request, config)
      if (error) throw new ServiceError(data)

      return data
    } catch (error) {
      errorManager.handleError(error)

      return []
    }
  }

  async function findPostalCodes(postalCode: string) {
    try {
      const endpoint = ENDPOINTS.GET_POSTAL_CODES
      const request = { filters: { postalCode } }
      const config = { mock: mock && servicesMock.GET_POSTAL_CODES }
      const { error, data } = await MiddlewareService.post(endpoint, request, config)
      if (error) throw new ServiceError(data)

      return data
    } catch (error) {
      errorManager.handleError(error)

      return { rows: [], total: 0 }
    }
  }

  async function findPostalCodesAuto(
    postalCode: string,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST
  ) {
    try {
      const endpoint = ENDPOINTS.POST_CP_CODES
      const dataRequest = { zip: postalCode }
      const config = { mock: mock && servicesMock.POST_CP_CODES }
      const { error, data } = await MiddlewareService.post(endpoint, dataRequest, config)
      if (error) throw new ServiceError(data)

      return data
    } catch (error) {
      const err = error instanceof CustomError ? error.setDisplayType(errorDisplayType) : error
      errorManager.handleError(err)

      return { rows: [], total: 0 }
    }
  }

  async function validateEmail(email: string) {
    try {
      const endpoint = ENDPOINTS.VALIDATE_EMAIL
      const request = { email }
      const config = { mock: mock && servicesMock.VALIDATE_EMAIL }
      const { error, data } = await MiddlewareService.post(endpoint, request, config)
      if (error) throw new ServiceError(data)

      return data
    } catch (error) {
      errorManager.handleError(error)

      return false
    }
  }

  async function getDocuments(
    productCode: string,
    businessLine: string,
    clientCode: string,
    language?: string
  ) {
    if (!productCode || !businessLine) {
      throw new Error('Invalid parameters')
    }

    const endpoint = ENDPOINTS.GET_ACCORDION_DOCUMENTS
    const request: any = { productCode, businessLine, clientCode: String(clientCode) }
    if (language) {
      request['language'] = language
    }
    const config = { mock: mock && servicesMock.GET_ACCORDION_DOCUMENTS }
    const { error, data } = await MiddlewareService.post(endpoint, request, config)
    if (error) throw new ServiceError(data)

    return data?.documentsList
  }

  async function fetchDocument(id: string, name: string, mimeType = 'application/pdf') {
    try {
      const endpoint = `${ENDPOINTS.DOWNLOAD_ACCORDION_DOCUMENT}/?id=${id}`
      const config = { mock: mock && servicesMock.DOWNLOAD_ACCORDION_DOCUMENT }
      const { error, data } = await MiddlewareService.get(endpoint, config)
      if (error) {
        throw new ServiceError({
          ...data,
          message: 'copy_fetch_document_service_error'
        })
      }

      return stringToFile(data, name, mimeType)
    } catch (error) {
      errorManager.handleError(error)
    }
  }

  async function downloadDocument(id: string, _name: string) {
    try {
      const endpoint = `${ENDPOINTS.DOWNLOAD_ACCORDION_DOCUMENT}/?id=${id}`
      const config = { mock: mock && servicesMock.DOWNLOAD_ACCORDION_DOCUMENT }
      const { error, data } = await MiddlewareService.get(endpoint, config)
      if (error) {
        throw new ServiceError({
          ...data,
          message: 'copy_fetch_document_service_error'
        })
      }

      const blob = base64ToBlob(data, 'application/pdf')
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch (error) {
      errorManager.handleError(error)
    }
  }

  async function downloadDocumentByReference(reference: string, _name: string) {
    try {
      const endpoint =
        mock && servicesMock.DOWNLOAD_DOCUMENT_BY_REFERENCE
          ? `${ENDPOINTS.DOWNLOAD_DOCUMENT_BY_REFERENCE}`
          : `${ENDPOINTS.DOWNLOAD_DOCUMENT_BY_REFERENCE}?reference=${reference}`
      const config = { mock: mock && servicesMock.DOWNLOAD_DOCUMENT_BY_REFERENCE }
      const { error, data } = await MiddlewareService.get(endpoint, config)
      if (error) {
        throw new ServiceError({
          ...data,
          message: 'copy_fetch_document_service_error'
        })
      }

      const blob = base64ToBlob(data.documentData, data.mimeType)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      // const a = document.createElement('a')
      // a.href = url
      // a.download = name //<- Descomentar si se hace el previsualizado
      // // a.target = '_blank' //<- Descomentar para previsualizar en nueva pestaña
      // document.body.appendChild(a)
      // a.click()
      // document.body.removeChild(a)
      // URL.revokeObjectURL(url)
    } catch (error) {
      errorManager.handleError(error)
    }
  }

  async function clickToCall(name: string, prefix: string, phone: string) {
    try {
      loadingStore.startLoading()
      const isPymes = (router.currentRoute.value.meta?.isPymes as boolean) ?? false
      const endpoint = ENDPOINTS.CLICK_TO_CALL
      const userInfo = getUserInfo()
      const request = {
        name,
        prefix,
        phone,
        businessLine: businessLineStore.getBusinessLine(),
        channel: userInfo.channel,
        ip: userInfo.ip,
        device: userInfo.browser,
        browser: userInfo.browserName,
        so: userInfo.os,
        resolution: userInfo.resolution,
        mobileManufacturer: 'UnKnown',
        mobileModel: 'UnKnown',
        microsite:
          window.origin +
          '/' +
          (isPymes
            ? import.meta.env.VITE_SERVER_PYMES_BASE_FOLDER
            : import.meta.env.VITE_SERVER_BASE_FOLDER) +
          router.currentRoute.value.fullPath.substring(1)
      }
      const config = { mock: mock && servicesMock.CLICK_TO_CALL }
      const { error, data } = await MiddlewareService.post(endpoint, request, config)
      if (error) {
        throw new ServiceError({
          ...data,
          message: 'copy_service_error',
          displayType: ErrorDisplayTypeEnum.WITHOUT_TOAST
        })
      }

      //toastStore.successToast('copy_thanks_we_call_u', true)
    } catch (error) {
      errorManager.handleError(error)

      throw ''
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function fetchDocumentsByRole(
    role: string
  ): Promise<{ documentType: string; documentDescription: string }[]> {
    try {
      const endpoint = `${ENDPOINTS.MDW_GET_DOCUMENTS_BY_ROLE}/${role}`
      const config = { mock: mock && servicesMock.MDW_GET_DOCUMENTS_BY_ROLE }
      const { error, data } = await MiddlewareService.get(endpoint, config)
      if (error) throw new ServiceError(data)

      return data
    } catch (error) {
      errorManager.handleError(error)

      return []
    }
  }

  async function sendPayment(
    quotationId: string,
    bankDetails: BankDetails,
    endpoint = ENDPOINTS.SAVE_PAYMENT
  ) {
    const request = CommonMapper.toPaymentRequest(quotationId, bankDetails)
    const config = { mock: mock && servicesMock.SAVE_PAYMENT }
    return await MiddlewareService.post(endpoint, request, config)
  }

  async function signDocument(
    quotationId: string,
    urlReturn: string
  ): Promise<{ id: string; urlSignature: string } | null> {
    try {
      const endpoint = ENDPOINTS.SING_DOCUMENT
      const request = { quotationId, urlReturn }
      const config = { mock: mock && servicesMock.SING_DOCUMENT }
      const { error, data } = await MiddlewareService.post(endpoint, request, config)
      if (error) throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.TOAST)

      return data
    } catch (error) {
      loadingStore.stopLoading()
      if (
        error instanceof ServiceError &&
        error?.code === ServiceErrorCodeEnum.SIGNATURE_WITHOUT_ALL_DOCUMENTS
      ) {
        error.setType(MessageEnum.WARNING)
      }

      errorManager.handleError(error)

      return null
    }
  }

  async function fetchInsuranceState(
    quotationId: string,
    hiringPage: string,
    endpoint = ENDPOINTS.POLICY_EMISSION
  ): Promise<number | null> {
    try {
      const { error, data } = await MiddlewareService.get(`${endpoint}/${quotationId}`)
      if (!error && data.codeStateEmission === InsuranceState.REJECTED) {
        router.push({ name: hiringPage, params: { id: quotationId } })
        return null
      }

      const validStates = [
        InsuranceState.ISSUED,
        InsuranceState.PENDING,
        InsuranceState.PENDING_EMISSION
      ]
      if (error || !validStates.includes(data.codeStateEmission))
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setTitle('copy_error_emission_service_title')
          .setModalDescription('copy_error_emission_service_description')
          .setExtra({ hiringNumber: businessLineStore.getHiringNumber() })
          .setButtons([
            new ModalButton({
              text: 'copy_button_emission_contact',
              callback: () => supportModalStore.setOpenModal(true)
            }),
            new ModalButton({
              text: 'copy_button_emission_exit',
              navTo: 'exit',
              type: ButtonTypeEnum.OUTLINE
            })
          ])
      return data.codeStateEmission
    } catch (error) {
      errorManager.handleError(error)

      return null
    }
  }

  async function sendEmailRequest(
    quotationId: string,
    email: string,
    privacyPolicyConsent: boolean
  ): Promise<void> {
    try {
      const endpoint = ENDPOINTS.MDW_SEND_EMAIL_REQUEST
      const request = { quotationId, to: email, privacyPolicyConsent } // todo - add in MID
      const config = { mock: mock && servicesMock.MDW_SEND_EMAIL_REQUEST }
      const { error, data } = await MiddlewareService.post(endpoint, request, config)
      if (error) throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)

      toastStore.successToast('Correo enviado correctamente')
    } catch (error) {
      errorManager.handleError(error)

      throw error
    }
  }

  async function loadCoverages({
    businessLine,
    insuranceType,
    clientCode,
    productCode
  }: {
    businessLine?: string | undefined
    insuranceType?: string | undefined
    clientCode?: string | undefined
    productCode?: string | undefined
  }): Promise<CoverageBasic[]> {
    try {
      const endpointMID = ENDPOINTS.GET_COVERAGES_COMPLETE

      const request = new GetCoveragesCompleteRequest({
        businessLine,
        insuranceType,
        clientCode,
        productCode
      })
      const { error: errorMID, data: dataMID } = await MiddlewareService.post(endpointMID, request)
      if (errorMID) {
        throw new ServiceError(dataMID).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      const coverages = (dataMID as GetCoveragesCompleteResponse).coverageInfoData
      const DXPCoveragesToRequest = coverages.map(({ coverageCode }) => ({ coverageCode }))
      const DXPCoveragesRequest =
        CommonCommonMapper.GetCoveragesBasicMapper.toRequest(DXPCoveragesToRequest)
      const endpointDXP = ENDPOINTS.DXP_GET_COVERAGES_BASIC
      const { error: errorDXP, data: dataDXP } = await DXPService.post(
        endpointDXP,
        DXPCoveragesRequest
      )
      if (errorDXP) {
        throw new ServiceError(dataDXP).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      const DXPCoveragesBasic = dataDXP as GetCoveragesBasicResponse[]
      return CommonCommonMapper.GetCoveragesBasicMapper.fromResponse(
        DXPCoveragesBasic,
        coverages
      ).sort((a: CoverageBasic, b: CoverageBasic) => a.coverageCode.localeCompare(b.coverageCode))
    } catch (error) {
      errorManager.handleError(error)

      throw error
    }
  }

  async function getMultipleQRFiles(quotationId: string, riskNumber?: number) {
    try {
      const endpoint = ENDPOINTS.GET_QR
      let url = `${endpoint}/${quotationId}`
      if (riskNumber) {
        url += `?riskNumber=${riskNumber}`
      }
      const { error, data } = await MiddlewareService.get(url)
      if (error) throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.TOAST)

      const documents = !riskNumber
        ? data.document
        : data.document.filter(
            (doc: { documentType: string }) =>
              doc.documentType === `justificante-${riskNumber}` || doc.documentType === '1'
          )
      if (!documents || documents.length === 0)
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.TOAST)

      return documents
    } catch (error) {
      errorManager.handleError(error)
      throw error
    }
  }

  async function getQRFile(quotationId: string, riskNumber?: number) {
    try {
      const endpoint = ENDPOINTS.GET_QR
      let url = `${endpoint}/${quotationId}`
      if (riskNumber) {
        url += `?riskNumber=${riskNumber}`
      }
      const { error, data } = await MiddlewareService.get(url)
      if (error) throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.TOAST)

      const document = !riskNumber
        ? data.document[0]
        : data.document.find(
            (doc: { documentType: string }) => doc.documentType === `justificante-${riskNumber}`
          )
      if (!document) throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.TOAST)

      return stringToFile(document.documentData, document.filename, document.mimeType)
    } catch (error) {
      errorManager.handleError(error)
      throw error
    }
  }

  async function getQRName(quotationId: string, riskNumber?: number) {
    try {
      const endpoint = ENDPOINTS.GET_QR_NAME
      let url = `${endpoint}/${quotationId}`
      if (riskNumber) {
        url += `?riskNumber=${riskNumber}`
      }
      const { error, data } = await MiddlewareService.get(url)
      if (error) throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.TOAST)

      return Array.isArray(data) && data.length === 0 ? null : data
    } catch (error) {
      errorManager.handleError(error)
      throw error
    }
  }

  async function saveMultiQR(quotationId: string, files: File[], riskNumber: number) {
    try {
      const endpoint = ENDPOINTS.SAVE_QR
      const documentData = await Promise.all(files.map((file) => fileToBase64(file)))
      const request = {
        id: quotationId,
        document: files.map((file, index) => ({
          filename: file.name,
          mimeType: file.type,
          documentType: `justificante-${riskNumber}`,
          documentData: documentData[index],
          ...(riskNumber ? { riskNumber } : {})
        }))
      }

      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }
    } catch (error) {
      errorManager.handleError(error)
      throw error
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function saveQR(quotationId: string, file: File, riskNumber?: string | null) {
    try {
      const endpoint = ENDPOINTS.SAVE_QR
      const documentData = await fileToBase64(file)
      const request = {
        id: quotationId,
        document: [
          {
            filename: file.name,
            mimeType: file.type,
            documentType: riskNumber ? `justificante-${riskNumber}` : '1',
            documentData,
            ...(riskNumber ? { riskNumber } : {})
          }
        ]
      }

      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }
    } catch (error) {
      errorManager.handleError(error)
      throw error
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function deleteQR(quotationId: string) {
    try {
      const endpoint = ENDPOINTS.DELETE_QR
      const { error, data } = await MiddlewareService.delete(`${endpoint}/${quotationId}`)
      if (error) throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.TOAST)
    } catch (error) {
      errorManager.handleError(error)
      throw error
    }
  }

  async function getHiringProcessFromRecovery(endpoint: string, quotationId: string) {
    try {
      if (!/^\/Recovery\/GetRecovery/.test(endpoint)) {
        throw new Error('Invalid endpoint')
      }

      const endpointToRequest = `${endpoint}?quotationId=${quotationId}`
      const { error, data } = await MiddlewareService.get(endpointToRequest)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      return data
    } catch (error) {
      errorManager.handleError(error)
      throw error
    }
  }

  async function getProductsDataFromDXP(products: { productCode: string; clientCode: string }[]) {
    try {
      const endpoint = ENDPOINTS.DXP_GET_PRODUCT
      const request = { language: 'es', products }
      const { error, data } = await DXPService.post(endpoint, request)
      if (error) throw new ServiceError(data)

      return data.map((d: any) => d.product)
    } catch (error) {
      errorManager.handleError(error)
      return []
    }
  }

  async function getInfoMediator(mediatorCode: string) {
    try {
      if (infoMediator.value && infoMediator.value.mediatorId === mediatorCode) {
        return infoMediator.value
      }

      const endpoint = ENDPOINTS.GET_INFO_MEDIATOR
      const config = {
        mock: loadingStore.isActiveMock() && servicesMock.GET_INFO_MEDIATOR
      }
      const { error, data } = await MiddlewareService.get(`${endpoint}/${mediatorCode}`, config)
      if (error) {
        throw new ServiceError(data)
      }

      const mediator = new InfoMediator(data)
      const hasAllowedBusinessLine = Object.values(mediator.allowedBusinessLines ?? {}).some(
        Boolean
      )
      if (!hasAllowedBusinessLine) {
        throw new Error('Mediator has no allowed business lines')
      }

      infoMediator.value = mediator
      return infoMediator.value
    } catch (error) {
      infoMediator.value = null
      return null
    }
  }

  async function resolveMediatorFromRecovery(mediatorCode?: string | null) {
    const normalizedCode = mediatorCode?.trim()
    if (!normalizedCode) {
      clearInfoMediator()
      return null
    }

    return await getInfoMediator(normalizedCode)
  }

  function clearInfoMediator() {
    infoMediator.value = null
  }

  function isBranchAllowedForMediator(branchName: string): boolean {
    if (!infoMediator.value) {
      return true
    }

    const mediatorBranchKey = MEDIATOR_BUSINESS_LINE_MAP[branchName]
    return !!mediatorBranchKey && !!infoMediator.value.allowedBusinessLines?.[mediatorBranchKey]
  }

  function getInvalidNames() {
    return ['FCO', 'MA']
  }

  function getInvalidSurnames() {
    return ['HDEZ', 'GLEZ', 'FDEZ', 'RGUEZ', 'JIMNEZ', 'MTNEZ', 'MTINEZ']
  }

  return {
    infoMediator,
    loaderMessage,
    showLogo,
    // DXP
    fetchProvinceCodes,
    fetchBranchesData,
    fetchPymesBranchesData,
    fetchFooterLinks,
    loadBranchesConfig,
    fetchCountries,
    fetchCountryPrefixes,
    businessLines,
    pymesBusinessLines,

    // MIDDLEWARE
    validateEmail,
    findCities,
    findStreets,
    normalizeAddress,
    findPostalCodes,
    findPostalCodesAuto,
    footerLinks,

    getDocuments,
    fetchDocument,
    downloadDocument,
    downloadDocumentByReference,
    clickToCall,
    fetchDocumentsByRole,
    sendPayment,
    signDocument,
    fetchInsuranceState,
    sendEmailRequest,
    loadCoverages,
    getQRFile,
    getQRName,
    saveQR,
    saveMultiQR,
    getMultipleQRFiles,
    deleteQR,
    //props
    getCommonConfigs,
    //User
    getUserInfo,
    //Referred
    getReferred,
    setReferred,
    //Cookies
    getUrlDxp,
    setUrlDxp,

    // Used in signature complete view
    getHiringProcessFromRecovery,
    getProductsDataFromDXP,

    getInvalidNames,
    getInvalidSurnames,
    getContactPhone,
    getInfoMediator,
    resolveMediatorFromRecovery,
    clearInfoMediator,
    isBranchAllowedForMediator
  }
})
